// =====================================================================
// Pillar A — C-SUITE
// Rich cards: role name + persona one-liner + descriptive blurb + a chip
// list of advanced operating metrics. The chips deliberately surface
// jargon-heavy metrics that owners often *don't* track themselves —
// signalling depth, not just generic dashboards.
// =====================================================================
const PILLAR_A = [
    {
        title: "CFO",
        key: "cfo",
        persona: "The money brain",
        blurb:
            "Sees cash before it dries up and margins before they slip. Runs your finance war-room every minute — collections, working capital, GST, and the next 13 weeks of cash — so you never run out of runway by surprise.",
        chips: [
            "CCC (Cash Cycle)",
            "DSO / DPO / DIO",
            "13-week cash forecast",
            "EBITDA & contribution margin",
            "Customer concentration risk",
            "GST ITC reconciliation",
        ],
        img: "/img/csuite/cfo.png",
        alt: "CFO at her cabin desk with finance dashboards",
    },
    {
        title: "CEO",
        key: "ceo",
        persona: "The decision room",
        blurb:
            "Sees the whole board on one screen and surfaces what to fix first. Composite health score, operational contribution by business line, scenario / what-if planning — the boardroom view you used to need a consulting firm for.",
        chips: [
            "Composite Health Score",
            "Op. contribution margin",
            "OKR & strategy tracking",
            "Risk heat-map",
            "Scenario / what-if planning",
            "Top-of-mind decisions board",
        ],
        img: "/img/csuite/ceo.png",
        alt: "CEO at his glass-cabin command centre",
    },
    {
        title: "CMO",
        key: "cmo",
        persona: "The growth engine",
        blurb:
            "Knows which customer makes you money and which one costs you. Cohort retention, unit economics, channel ROI, payback period — the marketing math that separates real growth from vanity revenue.",
        chips: [
            "CAC · LTV · LTV:CAC",
            "Cohort retention curves",
            "NRR / GRR",
            "Channel-level ROI",
            "Pipeline velocity",
            "Payback period",
        ],
        img: "/img/csuite/cmo.png",
        alt: "CMO in her marketing cabin with growth dashboards",
    },
    {
        title: "CTO",
        key: "cto",
        persona: "The systems brain",
        blurb:
            "Keeps uptime green, IT spend honest, and security tight. Watches every system you depend on — from servers to SaaS — and flags the leaks before your team notices the lag.",
        chips: [
            "SLA uptime / availability",
            "MTTR · MTBF",
            "Infra cost per transaction",
            "API latency (p95 / p99)",
            "Vulnerability score",
            "DAU / MAU & deploy frequency",
        ],
        img: "/img/csuite/cto.png",
        alt: "CTO in the R&D lab with engineering dashboards",
    },
];

const PILLAR_B = [
    {
        title: "Company Trajectory",
        desc: "Where you're headed",
        img: "/img/features/trajectory.png",
        alt: "Company trajectory chart",
    },
    {
        title: "Benchmarking",
        desc: "vs your peers",
        img: "/img/features/benchmarking.png",
        alt: "Peer benchmarking comparison",
    },
    {
        title: "Supplier Audits",
        desc: "GST + risk checks",
        img: "/img/features/supplier_audits.png",
        alt: "Supplier audit checklist",
    },
    {
        title: "Smart Alerts",
        desc: "Act before crisis",
        img: "/img/features/smart_alerts.png",
        alt: "Smart alerts notifications",
    },
    {
        title: "Forecasting",
        desc: "13-week cash view",
        img: "/img/features/forecasting.png",
        alt: "13-week cash flow forecast",
    },
];

const PILLAR_C = [
    {
        title: "GST Compliance",
        desc: "ITC reconciliation, filing reminders, GSTR-2B matching",
        img: "/img/features/gst.png",
        alt: "GST compliance illustration",
    },
    {
        title: "WhatsApp AI Co-pilot",
        desc: "Ask anything in Hindi or English, answered from your real data",
        img: "/img/features/whatsapp_ai.png",
        alt: "WhatsApp AI chat illustration",
    },
    {
        title: "Advanced AI Toolkit",
        desc: "Scenario planning, decision support, custom MIS reports",
        img: "/img/features/ai_toolkit.png",
        alt: "Advanced AI scenario tree",
    },
    {
        title: "Dynamic Content Injection",
        desc: "Every screen adapts to your business in real time. C-ORE injects the right insight, alert and next action into each dashboard, briefing and WhatsApp message based on your live data — so what you see is always specific to your numbers, never a generic template.",
        img: "/img/features/trajectory.png",
        alt: "Dynamic content adapting in real time to live business data",
    },
    {
        title: "A Boardroom That Adapts To Your Business",
        desc: "C-ORE dynamically changes what it monitors, prioritizes and reports based on your industry, business model, growth stage and operational profile. Every company gets a different boardroom experience because every business has different priorities.",
        img: "/img/features/benchmarking.png",
        alt: "Adaptive boardroom that reshapes around each business profile",
    },
];

function CSuiteCard({ item }) {
    return (
        <article
            className={`csuite__card csuite__card--${item.key} reveal`}
            data-testid={`csuite-${item.key}`}
        >
            <div className="csuite__media">
                <img src={item.img} alt={item.alt} loading="lazy" />
                <span className="csuite__tag">{item.title}</span>
            </div>
            <div className="csuite__body">
                <div className="csuite__head">
                    <h3 className="csuite__role">{item.title}</h3>
                    <span className="csuite__persona">{item.persona}</span>
                </div>
                <p className="csuite__blurb">{item.blurb}</p>
                <ul
                    className="csuite__chips"
                    aria-label={`${item.title} key metrics`}
                >
                    {item.chips.map((c, i) => (
                        <li key={i} className="csuite__chip">
                            {c}
                        </li>
                    ))}
                </ul>
            </div>
        </article>
    );
}

function FeatureCard({ item }) {
    return (
        <div className="feature__card reveal">
            <div className="feature__media">
                <img src={item.img} alt={item.alt} loading="lazy" />
            </div>
            <div className="feature__body">
                <div className="feature__title">{item.title}</div>
                <div className="feature__desc">{item.desc}</div>
            </div>
        </div>
    );
}

function Pillar({ badgeVariant, pillName, label, items, csuite, note }) {
    return (
        <div className="pillar">
            <div className="pillar__head reveal">
                <span className={`pillar__badge pillar__badge--${badgeVariant}`}>
                    {pillName}
                </span>
                <span className="pillar__label">{label}</span>
                <span className="pillar__rule" aria-hidden="true" />
            </div>
            <div className={csuite ? "csuite__grid" : "feature__grid"}>
                {items.map((it, i) =>
                    csuite ? (
                        <CSuiteCard key={i} item={it} />
                    ) : (
                        <FeatureCard key={i} item={it} />
                    ),
                )}
            </div>
            {note ? <p className="pillar__note reveal">{note}</p> : null}
        </div>
    );
}

export default function Product() {
    return (
        <section className="product" id="features" data-testid="v2-product">
            <div className="container">
                <div className="product__head reveal">
                    <span className="product__eyebrow">The Product</span>
                    <h2 className="product__title">
                        One platform. Three layers of intelligence.
                    </h2>
                    <p className="product__sub">
                        Everything an MSME needs to run, scale, and protect
                        itself — built into a single co-founder.
                    </p>
                </div>

                <Pillar
                    badgeVariant="a"
                    pillName="A · C-Suite"
                    label="Your leadership team, on call 24/7"
                    items={PILLAR_A}
                    csuite
                />
                <Pillar
                    badgeVariant="b"
                    pillName="B · C-ORE Intelligence"
                    label="Foresight and protection"
                    items={PILLAR_B}
                />
                <Pillar
                    badgeVariant="c"
                    pillName="C · Compliance + AI"
                    label="Compliance and advanced AI toolkit"
                    items={PILLAR_C}
                    note="The parameters C-ORE tracks evolve automatically as your business profile changes. Manufacturing businesses, agencies, retailers, consultants and service companies each receive a tailored boardroom built around the metrics that matter most to them."
                />
            </div>
        </section>
    );
}
